import React, { useEffect, useMemo } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HPButton from "@components/common/Button";
import HPDropdown from "@components/common/Dropdown";
import { useGetDatasetByIdQuery } from "@store/api/dataset-api";
import type { VisualizationBase } from "@models/VisualizationBase";
import BaseConditionSummaryCard from "./BaseConditionSummaryCard";

interface PreconfiguredBaseContainerProps {
  datasetId: string | null;
  isDisabled?: boolean;
  base: VisualizationBase;
  onChange: (base: VisualizationBase) => void;
}

const noop = () => {};

// Lets the researcher link the widget to a Dataset-Level Base: a Base dropdown +
// read-only condition cards. Conditions are never copied onto the widget — only
// the id (+ cached name) is stored; resolution happens at query time.
const PreconfiguredBaseContainer: React.FC<PreconfiguredBaseContainerProps> = ({
  datasetId,
  isDisabled,
  base,
  onChange,
}) => {
  const { data: dataset, isLoading, isError, refetch } = useGetDatasetByIdQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  const bases = useMemo(
    () => [...(dataset?.bases ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [dataset?.bases],
  );

  const selected = useMemo(
    () => bases.find((b) => b.id === base.preconfiguredBaseId),
    [bases, base.preconfiguredBaseId],
  );

  // Once the catalog has loaded successfully, reconcile the stored reference:
  // drop a selection that no longer exists (e.g. the dataset changed), and keep
  // the cached display name current. Never react to a transient load error.
  useEffect(() => {
    if (isLoading || isError || !dataset) return;
    if (!base.preconfiguredBaseId) return;
    if (!selected) {
      onChange({ ...base, preconfiguredBaseId: undefined, preconfiguredBaseName: undefined });
    } else if (selected.name !== base.preconfiguredBaseName) {
      onChange({ ...base, preconfiguredBaseName: selected.name });
    }
  }, [isLoading, isError, dataset, selected, base, onChange]);

  const options = useMemo(
    () => bases.map((b) => ({ value: b.id, label: b.name })),
    [bases],
  );

  const containerSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mt: 2,
    p: 2,
    border: "1px solid #eee",
    borderRadius: 1,
  } as const;

  if (isLoading) {
    return (
      <Box sx={{ ...containerSx, alignItems: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={containerSx}>
        <Typography variant="body2" color="error">
          Couldn't load the dataset's bases.
        </Typography>
        <HPButton
          ariaLabel="Retry loading bases"
          variant="outlined"
          color="primary"
          onClick={() => refetch()}
        >
          Retry
        </HPButton>
      </Box>
    );
  }

  const hasBases = bases.length > 0;

  return (
    <Box sx={containerSx}>
      <HPDropdown
        label="Base"
        value={base.preconfiguredBaseId ?? ""}
        onChange={(id: string) => {
          const found = bases.find((b) => b.id === id);
          onChange({
            ...base,
            preconfiguredBaseId: id,
            preconfiguredBaseName: found?.name,
          });
        }}
        options={options}
        placeholder="Select a base"
        disabled={isDisabled || !hasBases}
      />

      {!hasBases && (
        <Typography variant="caption" color="text.secondary">
          Please create a base in the dataset editor first.
        </Typography>
      )}

      {selected && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {selected.conditions.map((condition, index) => (
            <BaseConditionSummaryCard
              key={condition.id}
              datasetId={datasetId as string}
              condition={condition}
              index={index}
              disabled
              onEdit={noop}
              onDelete={noop}
            />
          ))}
        </Box>
      )}

      {datasetId && (
        <Link
          href={`/datasets/${datasetId}/config`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="caption"
          color="primary"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          Manage in Dataset Configuration
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </Link>
      )}
    </Box>
  );
};

export default PreconfiguredBaseContainer;
